import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import CursorOverlay from './CursorOverlay';
import CursorTrailLayer from './CursorTrailLayer';
import FlyToCartLayer from './FlyToCartLayer';
import OnomatopoeiaLayer from './OnomatopoeiaLayer';
import ScrollSfxLayer from './ScrollSfxLayer';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { useFxEnabled } from './fxPreference';

type SpawnFlyFn = (sourceEl: HTMLElement, targetEl: HTMLElement, imageSrc: string) => void;

interface AnimeFxContextValue {
  /** False when prefers-reduced-motion is reduce, or the guest switched the animations off. Stays true in SSR/jsdom (matchMedia absent → no preference detected). */
  fxEnabled: boolean;
  /** False on touch devices (no fine pointer) — gates the cursor SVG only. */
  cursorEnabled: boolean;
  /** Mutable ref the cart button registers itself into. Read at fly-spawn time. */
  cartTargetRef: { current: HTMLElement | null };
  /** Set by FlyToCartLayer when it mounts. Calling before mount is a no-op. */
  spawnFlyRef: { current: SpawnFlyFn | null };
}

const AnimeFxContext = createContext<AnimeFxContextValue | null>(null);

export function useAnimeFxContext(): AnimeFxContextValue {
  const ctx = useContext(AnimeFxContext);
  if (!ctx) throw new Error('useAnimeFxContext must be used within AnimeFxProvider');
  return ctx;
}

function safeMatch(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia(query).matches; } catch { return false; }
}

interface Props {
  /** Carnet du serveur : la decoration sonore et comique s'eteint. Chaque appui
   *  sur un bouton faisait eclater une onomatopee ET jouer un « clic » Web
   *  Audio ; en plein service, a trente appuis par table, c'est du bruit sur la
   *  fiche que le serveur vient de toucher. Le vol-vers-panier reste : depuis
   *  que l'ajout n'ouvre plus le tiroir, c'est son seul accuse de reception. */
  staffMode?: boolean;
  children: ReactNode;
}

export function AnimeFxProvider({ staffMode = false, children }: Props) {
  const animations = useFxEnabled();
  const [reducedMotion, setReducedMotion] = useState(() => safeMatch('(prefers-reduced-motion: reduce)'));
  const [pointerFine, setPointerFine] = useState(() => safeMatch('(pointer: fine)'));
  const cartTargetRef = useRef<HTMLElement | null>(null);
  const spawnFlyRef = useRef<SpawnFlyFn | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const pointer = window.matchMedia('(pointer: fine)');
    const onMotion = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    const onPointer = (e: MediaQueryListEvent) => setPointerFine(e.matches);
    motion.addEventListener?.('change', onMotion);
    pointer.addEventListener?.('change', onPointer);
    return () => {
      motion.removeEventListener?.('change', onMotion);
      pointer.removeEventListener?.('change', onPointer);
    };
  }, []);

  // The footer switch (fxPreference) is the guest's own kill switch: it turns
  // off every decorative layer at once, exactly like prefers-reduced-motion.
  const value = useMemo<AnimeFxContextValue>(() => ({
    fxEnabled: !reducedMotion && animations,
    cursorEnabled: !reducedMotion && animations && pointerFine,
    cartTargetRef,
    spawnFlyRef,
  }), [reducedMotion, animations, pointerFine]);

  // Premium style mode drops the cartoon FX: manga hand cursor, the cursor
  // trail and the onomatopoeia bursts. Fly-to-cart and scroll SFX stay —
  // they are interaction feedback, not decoration.
  const playful = useStyleMode() !== 'premium' && !staffMode;

  return (
    <AnimeFxContext.Provider value={value}>
      {children}
      {value.cursorEnabled && playful && <CursorOverlay />}
      {value.cursorEnabled && playful && <CursorTrailLayer />}
      {value.fxEnabled && playful && <OnomatopoeiaLayer />}
      {value.fxEnabled && <FlyToCartLayer />}
      {value.fxEnabled && !staffMode && <ScrollSfxLayer />}
    </AnimeFxContext.Provider>
  );
}
